// The UI speaks the account's language. `account.language` is shared with
// Telegram (it will come from the ORCA API); this gate applies it before the
// shell renders, keeps <html lang> in sync and, when it changes, refetches the
// content that the server localizes (news, Orca AI, Signals…) — once the
// server has the new language (an optimistic switch waits for its answer).
import { useIsMutating, useQueryClient } from '@tanstack/react-query';
import { useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';
import i18n from '@/i18n';
import { useAccount } from '@/hooks/queries';

export function LanguageGate({ children }: { children: ReactNode }) {
  // the account query is the ORCA API's account (the DEV mock only with ?dev=): one source of the language
  const { language } = useAccount();
  const qc = useQueryClient();
  // the first render already speaks the account's language; a later switch is applied before
  // paint (not during render: other components listen to i18n and re-render on the change)
  const mounted = useRef(false);
  if (!mounted.current && i18n.language !== language) void i18n.changeLanguage(language);
  useLayoutEffect(() => { mounted.current = true; if (i18n.language !== language) void i18n.changeLanguage(language); }, [language]);
  const saving = useIsMutating({ mutationKey: ['account'] });
  const prev = useRef(language);
  useEffect(() => {
    document.documentElement.lang = language;
    if (prev.current === language || saving) return;
    prev.current = language;
    void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== 'account' });
  }, [language, saving, qc]);
  return <>{children}</>;
}
